/**
 * The generated OpenAPI documentation for one endpoint — the whole surface, in
 * one component (task 821).
 *
 * This was the body of `EndpointApiDocsTab` until the Developer Info tab needed
 * the same thing. It is lifted out rather than copied because what it does is
 * not a layout: it projects the endpoint into an OpenAPI 3.1 document
 * client-side, keys that projection off the environment list, and hands the
 * result both to Swagger UI and to the raw/copy/download controls. Two copies of
 * that would be two answers to "what is this service's contract".
 *
 * **The server dropdown is the point of passing environments.** Since task 799
 * the document carries one `servers` entry per deployment environment — plain
 * URL first, labelled by what it actually serves, then each environment in
 * promotion order — and Swagger's server picker is what turns that array into
 * the dropdown a developer chooses a stage from. `endpointToOpenApi` owns that
 * shape and every URL in it; nothing here composes a URL.
 *
 * With no environments, or the lever off, `serverEnvironments` is empty and the
 * projection returns byte-for-byte the single-server document it always did.
 *
 * `showEnvironmentUrls` exists because the two hosts differ in exactly one way.
 * The Advanced page's API Docs tab is the only place that endpoint's URLs
 * appear, so it carries the list. The Developer Info tab already shows the same
 * `EnvironmentUrlList` in its API section, one rail click away — a second copy
 * there would be the same table twice on one tab.
 */
import { useMemo, useState } from 'react';
import {
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import type { Endpoint } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { useVersioningEnabled } from '@/hooks/useResourceVersions';
import { EnvironmentUrlList } from '@/components/environments/EnvironmentUrlList';
import { SwaggerUiView } from './SwaggerUiView';
import { endpointToOpenApiDocument, openApiToJson } from '@/utils/endpointToOpenApi';

type DocView = 'rendered' | 'raw';

interface ApiDocsPanelProps {
  endpoint: Endpoint;
  /**
   * Mount the shared environment URL list above the document. The Advanced
   * page's API Docs tab does; Developer Info does not, because its API section
   * already carries that list.
   */
  showEnvironmentUrls?: boolean;
  /** One line under the heading, so each host can say why the docs are here. */
  subtitle?: string;
  testId?: string;
}

export function ApiDocsPanel({
  endpoint,
  showEnvironmentUrls = false,
  subtitle = 'This endpoint expressed as an OpenAPI 3.1 contract.',
  testId = 'api-docs-panel',
}: ApiDocsPanelProps) {
  const toast = useToast();
  const [view, setView] = useState<DocView>('rendered');
  const [copied, setCopied] = useState<'json' | null>(null);

  // Same lever as the Releases surface; it keeps its `…rollout` name.
  const environmentsEnabled = useFlag('innovate.fe.environments.rollout');
  const { environments } = useEnvironments({ enabled: environmentsEnabled });
  const serverEnvironments = environmentsEnabled ? environments : [];
  // WHAT THE PLAIN URL SERVES IS THE ACCOUNT'S SETTING (task 853). The
  // projection is pure and takes it as an argument; this is the surface that
  // holds it, so this is where it is read.
  const { enabled: publishingEnabled } = useVersioningEnabled();
  // THE DOMAINS THE ACCOUNT IS SERVING ON (task 857). Appended to `servers`
  // behind the platform entries: these docs are read inside the app, so Try it
  // out defaults to the address the platform owns and the custom domain is one
  // more entry in the picker. An account with none gets the same document as
  // before.
  const customDomainHostnames = useActiveCustomDomainHostnames();

  const doc = useMemo(
    () =>
      endpointToOpenApiDocument(
        endpoint,
        serverEnvironments,
        publishingEnabled,
        customDomainHostnames
      ),
    // The hooks return fresh arrays each render, so the projection keys off what
    // actually distinguishes one environment list (or domain list) from another.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      endpoint,
      serverEnvironments.map(environment => environment.name).join(','),
      publishingEnabled,
      customDomainHostnames.join(','),
    ]
  );
  const json = useMemo(() => openApiToJson(doc), [doc]);

  const handleCopy = async (text: string, which: 'json') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'openapi.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Downloaded openapi.json');
  };

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Header: title + view toggle + actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              OpenAPI Document
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => setView('rendered')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  view === 'rendered'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Rendered
              </button>
              <button
                onClick={() => setView('raw')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  view === 'raw'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Raw JSON
              </button>
            </div>
            <button
              onClick={() => handleCopy(json, 'json')}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {copied === 'json' ? (
                <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-green-500" />
              ) : (
                <ClipboardDocumentIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              )}
              Copy
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* The environment URLs, above the spec. Renders nothing at all when the
          Account has no environments or the lever is off. */}
      {showEnvironmentUrls && <EnvironmentUrlList endpoint={endpoint} />}

      {view === 'rendered' ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-4">
          <SwaggerUiView spec={doc} />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <pre className="overflow-x-auto rounded-md bg-gray-900 dark:bg-gray-950 p-4 text-xs text-gray-100 font-mono max-h-[32rem]">
            <code>{json}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

export default ApiDocsPanel;
